import { useGetAllUsersQuery } from "app/services/api/usersApi";
import { Text, View } from "react-native";
import { useState } from "react";
import { CreateEditUserModal } from "../../CreateEditUserModal";
import { AddIcon, Icon } from "ui/gluestack/icon";
import { Button, ButtonIcon, ButtonText } from "ui/gluestack/button";
import TableList from "app/components/organisms/TableList";
import { AuthUserModel } from "app/services/api/types/authorisation/models/AuthUserModel";
import { ProfileAvatar } from "app/components/atoms/ProfileAvatar";
import { UserListActionButtons } from "../organism/UserListActionButtons";
import CardItem from "app/components/organisms/CardItem";
import { UserListContent } from "../organism/UserListContent";
import Pagination from "ui/molecules/pagination/Pagination";
import { ColumnModel } from "app/services/api/types/common/models/ColumnModel";
import usePagination from "ui/molecules/pagination/usePagination";
import { DeleteRoleUserModal } from "../../DeleteRoleUserModal";
import { PermissionCheck } from "app/features/permission/atoms/PermissionCheck";
import { PermissionsEnums } from "app/services/api/types/authorisation/enums/PermissionsEnums";
import { Portal } from "ui/modules/ThrelsPortal/Portal";

export default function UserListTemplate() {
  const [isModalVisible, setModalVisible] = useState(false);
  const [isDeleteModalVisible, setDeleteModalVisible] = useState(false);
  const [editUser, setEditUser] = useState<AuthUserModel | undefined>(
    undefined,
  );
  const [deleteUser, setDeleteUser] = useState<AuthUserModel | undefined>(
    undefined,
  );
  const { currentPage, handlePageChange } = usePagination();
  const { data, isFetching, refetch } = useGetAllUsersQuery({
    page: currentPage,
  });

  const toggleModal = () => {
    setEditUser(undefined);
    setModalVisible((prevState) => !prevState);
  };

  const startEditUser = (user: AuthUserModel) => {
    setEditUser(user);
    setModalVisible((prevState) => !prevState);
  };

  const handleSuccess = () => {
    setModalVisible(!isModalVisible);
    refetch();
  };

  const startDeleteUser = (user: AuthUserModel) => {
    setDeleteUser(user);
    setDeleteModalVisible(true);
  };

  const handleDeleteSuccess = () => {
    setDeleteModalVisible(false);
    refetch();
  };

  const columns: ColumnModel<AuthUserModel>[] = [
    {
      title: "Name",
      dataKey: "first_name",
      renderCell: (user: AuthUserModel) => (
        <View className="flex flex-row items-center">
          <ProfileAvatar user={user} />
        </View>
      ),
      colSpan: 5,
    },
    {
      title: "Role",
      dataKey: "roles",
      renderCell: (user: AuthUserModel) => (
        <Text className="text-xs text-typography-600 text-left">
          {user?.roles?.length
            ? user.roles.map((role) => role.display_name).join(", ")
            : "Role(s) unavailable"}
        </Text>
      ),
      colSpan: 2,
    },
    {
      title: "Location(s)",
      dataKey: "locations",
      renderCell: (user: AuthUserModel) => (
        <View className="flex flex-col">
          <Text className="text-xs text-typography-600 text-left">
            {user?.locations?.length
              ? user.locations.map((location) => location.name).join(", ")
              : "Location(s) unavailable"}
          </Text>
        </View>
      ),
      colSpan: 3,
    },
    {
      title: undefined,
      renderCell: (user: AuthUserModel) => (
        <UserListActionButtons
          user={user}
          onEdit={startEditUser}
          onDelete={startDeleteUser}
        />
      ),
      colSpan: 2,
    },
  ];

  return (
    <View className="flex-1">
      <CreateEditUserModal
        isVisible={isModalVisible}
        onClose={() => setModalVisible(!isModalVisible)}
        onSuccess={handleSuccess}
        initialUser={editUser}
      />
      <DeleteRoleUserModal
        isVisible={isDeleteModalVisible}
        onClose={() => setDeleteModalVisible(false)}
        user={deleteUser}
        onSuccess={handleDeleteSuccess}
      />
      <Portal hostName="headerButton" identifier="userList">
        <PermissionCheck
          permission={PermissionsEnums.CREATE_USER}
          fallback={<></>}
        >
          <Button
            action="primary"
            size="sm"
            className="mb-3"
            onPress={toggleModal}
          >
            <ButtonText>Create user</ButtonText>
            <ButtonIcon as={AddIcon} className="w-4 h-4" />
          </Button>
        </PermissionCheck>
      </Portal>
      <TableList
        columns={columns}
        data={data?.data ?? []}
        isFetching={isFetching}
        emptyMessage="No users available."
        mobileRenderItem={(item) => (
          <CardItem
            key={item.id}
            actionButtons={
              <UserListActionButtons
                user={item}
                onEdit={startEditUser}
                onDelete={startDeleteUser}
              />
            }
            cardContent={<UserListContent user={item} />}
          />
        )}
      />

      <Pagination meta={data?.meta} onPageChange={handlePageChange} />
    </View>
  );
}
